import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Star, MapPin, Clock, Plane, Hotel, Map, Package, FileText, Headphones } from 'lucide-react';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';
import { OfferBanner } from '../components/OfferBanner';
import { ReviewDialog } from '../components/ReviewDialog';
import { useLang } from '../context/LanguageContext';

const Home = ({ user }) => {
  const navigate = useNavigate();
  const { t, loc } = useLang();
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get('/destinations').then((res) => setDestinations(res.data)).catch(() => {});
    api.get('/packages').then((res) => setPackages(res.data)).catch(() => {});
    api.get('/reviews?limit=6').then((res) => setReviews(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <OfferBanner />
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-teal-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" data-testid="hero-title">
            <span className="block bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{t('heroTitle')}</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">{t('heroSub')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/destinations')}
              className="bg-sun-500 hover:bg-sun-600 text-white px-8 py-6 text-lg shadow-lg shadow-sun-500/30"
            >
              {t('exploreDestinations')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/packages')}
              className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg"
            >
              {t('viewPackages')}
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Destinations</h2>
            <p className="text-gray-600 text-lg">Handpicked destinations for your perfect getaway</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinations.slice(0, 6).map((destination) => (
              <Card
                key={destination.id}
                data-testid={`home-destination-${destination.name.toLowerCase()}`}
                className="group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white"
                onClick={() => navigate('/destinations')}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full flex items-center space-x-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-semibold">{destination.rating}</span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{loc(destination, 'name')}</h3>
                  <div className="flex items-center text-gray-600 mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">{destination.location}</span>
                  </div>
                  <p className="text-gray-600 mb-4 line-clamp-2">{destination.description}</p>
                  <div className="flex items-center justify-between">
                    <Price inr={destination.price} className="text-xl text-teal-600 font-semibold" />
                    <div className="flex items-center text-gray-500 text-sm">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{destination.duration}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/destinations')}
              variant="outline"
              className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg"
            >
              View All Destinations
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('ourServices')}</h2>
            <p className="text-gray-600 text-lg">Comprehensive travel solutions for your perfect journey</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Map, title: 'Customized Itineraries', desc: 'Personalized travel plans tailored to your preferences' },
              { icon: Hotel, title: 'Hotel Bookings', desc: 'Best accommodations from budget to luxury' },
              { icon: Plane, title: 'Flight Reservations', desc: 'Competitive airfare deals with major airlines' },
              { icon: Package, title: 'Tour Packages', desc: 'All-inclusive packages with sightseeing' },
              { icon: FileText, title: 'Visa Assistance', desc: 'Complete support for visa documentation' },
              { icon: Headphones, title: '24/7 Support', desc: 'Round-the-clock customer service' },
            ].map((service, idx) => (
              <Card key={idx} className="bg-white hover:shadow-xl transition-shadow duration-300 border-0">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Packages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Popular Packages</h2>
            <p className="text-gray-600 text-lg">All-inclusive packages for a hassle-free vacation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {packages.slice(0, 4).map((pkg) => (
              <Card
                key={pkg.id}
                data-testid={`home-package-${pkg.id}`}
                className="group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-300 border-0"
                onClick={() => navigate(`/packages/${pkg.id}`)}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{loc(pkg, 'name')}</h3>
                  <div className="flex items-center justify-between mb-4">
                    <Price inr={pkg.price} className="text-teal-600 font-semibold text-xl" />
                    <div className="flex items-center text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{pkg.duration}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pkg.includes.slice(0, 4).map((item, idx) => (
                      <span key={idx} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
                        {item}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/packages')}
              variant="outline"
              className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg"
            >
              View All Packages
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-3">{t('testimonials')}</h2>
              <p className="text-gray-600 text-lg">{t('testimonialsSub')}</p>
            </div>
            {user ? (
              <ReviewDialog onCreated={(r) => setReviews((prev) => [r, ...prev].slice(0, 6))} />
            ) : (
              <Button data-testid="review-login-btn" variant="outline" onClick={() => navigate('/login')} className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50">
                {t('loginToReview')}
              </Button>
            )}
          </div>

          {reviews.length === 0 ? (
            <p className="text-center text-gray-500" data-testid="reviews-empty">Be the first to share your TripSoul experience.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="reviews-grid">
              {reviews.map((review) => (
                <Card key={review.review_id} data-testid={`review-card-${review.review_id}`} className="bg-white border-0 hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <img
                        src={review.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.name)}&background=0d9488&color=fff`}
                        alt={review.name}
                        className="w-12 h-12 rounded-full mr-4 object-cover"
                      />
                      <div>
                        <h4 className="font-semibold text-gray-900">{review.name}</h4>
                        <p className="text-sm text-gray-600">
                          {[review.location, review.destination && `Trip to ${review.destination}`].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-600 italic">"{review.text}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">{t('readyTitle')}</h2>
          <p className="text-xl mb-8 text-teal-50">
            Let us help you create unforgettable memories with our expertly crafted travel packages
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/contact')}
            className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
          >
            Contact Us Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
